<section class="page-shell">
  <header class="page-hero dbw-card dbw-card--xl">
    <div>
      <p class="eyebrow">Batch</p>
      <h3>Traitement séquentiel des corrections</h3>
    </div>

    @if (currentItem()) {
      <div class="hero-actions">
        <oui-button
          type="outlined"
          color="secondary"
          (click)="reloadCurrentMetadata()"
          [disabled]="workspaceLoading() || previewLoading() || executing()">
          Recharger la metadata
        </oui-button>
        <oui-button
          type="outlined"
          color="secondary"
          (click)="openCurrentMetadata()">
          Ouvrir la vue complète
        </oui-button>
        <oui-button
          iconSuffix="arrow_forward"
          (click)="moveToNext()"
          [disabled]="!canMoveToNext()">
          Passer à la suivante
        </oui-button>
      </div>
    }
  </header>

  @if (loading()) {
    <dbw-state-card appearance="loading">
      <p>Chargement de la file batch.</p>
    </dbw-state-card>
  }

  @if (error() && !loading()) {
    <dbw-state-card
      appearance="error"
      title="Chargement impossible">
      <p>{{ error() }}</p>
    </dbw-state-card>
  }

  @if (!loading() && !error() && !hasItems()) {
    <section class="empty-state panel dbw-card dbw-card--xl">
      <h4>Aucune metadata candidate</h4>
      <p>Le reporting courant ne contient aucune correction à enchaîner.</p>
    </section>
  }

  @if (!loading() && !error() && hasItems() && currentItem(); as item) {
    <dbw-summary-grid [items]="summaryItems()"></dbw-summary-grid>
    <section class="content-grid">
      <article class="panel queue-panel dbw-card dbw-card--xl">
        <div class="panel-heading">
          <div>
            <h4>File de traitement</h4>
            <p>Les metadata terminées restent consultables mais ne bloquent plus la suite.</p>
          </div>
        </div>
        <div
          class="queue-grid"
          #queueContainer>
          @for (queueItem of items(); track queueItem.metadataId; let index = $index) {
            <button
              #queueCardRef
              type="button"
              class="queue-card dbw-card dbw-card--lg"
              [class.active]="index === currentIndex()"
              (click)="selectItem(index)">
              <div class="queue-card__header">
                <strong>Metadata {{ queueItem.metadataId }}</strong>
                <oui-tag
                  type="status"
                  [status]="getQueueItemStatus(queueItem.metadataId, index)">
                  {{ getQueueItemLabel(queueItem.metadataId, index) }}
                </oui-tag>
              </div>
              <span>{{ queueItem.investorCount }} investisseur(s)</span>
              <span>{{ queueItem.avancementCount }} avancement(s)</span>
            </button>
          }
        </div>
      </article>
      <section class="workspace-stack">
        <article class="panel focus-panel dbw-card dbw-card--xl">
          <div class="panel-heading">
            <div>
              <h4>Correction rapide de la metadata {{ item.metadataId }}</h4>
              <p>
                {{ item.displayNames.join(', ') || 'Investisseurs non renseignés' }}
              </p>
            </div>
          </div>
          @if (workspaceLoading()) {
            <dbw-state-card
              appearance="loading"
              [compact]="true">
              <p>Chargement de la metadata en cours.</p>
            </dbw-state-card>
          }
          @if (workspaceError() && !workspaceLoading()) {
            <dbw-state-card
              appearance="error"
              [compact]="true">
              <p>{{ workspaceError() }}</p>
            </dbw-state-card>
          }
          @if (workspace(); as vm) {
            <div
              class="status-banner dbw-card dbw-card--lg"
              [class.status-banner--done]="isCurrentCompleted()">
              <div>
                <strong>{{ vm.summaryMessage }}</strong>
                @if (!vm.hasDuplicates) {
                  <p>Cette metadata peut être considérée comme traitée dans la file batch.</p>
                }
                @if (vm.hasDuplicates) {
                  <p>Le groupe de doublons prioritaire est présenté ci-dessous pour accélérer la correction.</p>
                }
              </div>
              @if (isCurrentCompleted()) {
                <div class="status-banner__actions">
                  <oui-button
                    iconSuffix="arrow_forward"
                    (click)="moveToNext()"
                    [disabled]="!canMoveToNext()">
                    Passer à la suivante
                  </oui-button>
                </div>
              }
            </div>
            @if (vm.hasInvestorPersonMappingConflicts) {
              <dbw-state-card
                appearance="error"
                [compact]="true">
                <p>
                  {{
                    vm.investorPersonMappingConflictMessage ||
                      'La correction est bloquée: plusieurs personnes physiques Atlas sont liées à certains investisseurs.'
                  }}
                </p>
                <dbw-investor-person-mapping-conflicts-table
                  [conflicts]="vm.investorPersonMappingConflicts"></dbw-investor-person-mapping-conflicts-table>
              </dbw-state-card>
            }
            @if (vm.hasDuplicates) {
              @if (vm.duplicateAnalysis.firstDuplicateGroup; as firstGroup) {
                <section class="duplicate-banner dbw-card dbw-card--lg">
                  <div>
                    <strong>{{ firstGroup.matchLabel }} : {{ firstGroup.matchValue }}</strong>
                    @if (firstGroup.requiresManualVerification) {
                      <p>Vérification manuelle requise : les meilleurs scores sont proches.</p>
                    }
                  </div>
                  <oui-tag
                    type="status"
                    [status]="firstGroup.requiresManualVerification ? 'pending' : 'to-correct'">
                    {{ firstGroup.rankedCandidates.length }} candidat(s)
                  </oui-tag>
                </section>
              }
              <div class="candidate-grid">
                @for (candidate of firstGroupCandidates(); track candidate.investor.investorId) {
                  <article class="candidate-card dbw-card dbw-card--lg">
                    <div class="candidate-card__header">
                      <dbw-investor-card
                        [id]="candidate.investor.investorId"
                        [code]="candidate.investor.investorCode || 'Code inconnu'"
                        [name]="candidate.investor.displayName || 'Investisseur sans nom'"
                        [statusCode]="candidate.investor.statusCode"></dbw-investor-card>
                      <div class="candidate-score">
                        <div class="score-header">
                          <span>Score</span>
                        </div>
                        <div class="score-body">
                          <strong>{{ candidate.totalScore }}</strong>
                          <dbw-investor-deletion-score-details
                            [investorId]="candidate.investor.investorId"
                            [evaluations]="candidate.evaluations"></dbw-investor-deletion-score-details>
                        </div>
                      </div>
                    </div>
                    <div class="candidate-card__facts">
                      <dbw-investor-status-tag [statusCode]="candidate.investor.statusCode"></dbw-investor-status-tag>
                      <div class="ged-document-cell">
                        <span class="candidate-card__fact-label">Documents GED</span>
                        <span>{{ formatGedDocumentCount(candidate.investor.gedDocumentCount) }}</span>
                        @if (hasGedDocumentNames(candidate.investor)) {
                          <oui-infos-bulle [bulleTitle]="getGedDocumentNamesTitle(candidate.investor)">
                            @for (documentName of candidate.investor.gedDocumentNames; track documentName + $index) {
                              <div class="ged-document-name">
                                {{ documentName }}
                              </div>
                            }
                          </oui-infos-bulle>
                        }
                      </div>
                      <div class="ged-document-cell">
                        <span class="candidate-card__fact-label">Opérations Box</span>
                        <oui-tag
                          type="status"
                          size="big"
                          [status]="getBoxPlacementOperationsStatus(candidate.investor.hasBoxPlacementOperations)">
                          {{ getBoxPlacementOperationsLabel(candidate.investor.hasBoxPlacementOperations) }}
                        </oui-tag>
                      </div>
                      <div class="ged-document-cell">
                        <span class="candidate-card__fact-label">Intention option Immo</span>
                        <oui-tag
                          type="status"
                          size="big"
                          [status]="getImmoOptionIntentionStatus(candidate.investor.hasImmoOptionIntention)">
                          {{ getImmoOptionIntentionLabel(candidate.investor.hasImmoOptionIntention) }}
                        </oui-tag>
                      </div>
                    </div>
                    <div class="candidate-card__meta">
                      <oui-tag
                        type="status"
                        [status]="candidate.isAutomaticSuggestion ? 'completed' : 'pending'">
                        {{ candidate.isAutomaticSuggestion ? 'Suggestion automatique' : 'À confirmer' }}
                      </oui-tag>
                    </div>
                    @if (canExecuteMetadataCorrectionAction()) {
                      <oui-button
                        type="outlined"
                        color="secondary"
                        iconSuffix="play_arrow"
                        [disabled]="correctionBlocked() || previewLoading() || executing()"
                        (click)="selectCandidate(candidate)">
                        Choisir cet investisseur
                      </oui-button>
                    }
                  </article>
                }
              </div>
              <div class="preview-panel dbw-card dbw-card--lg">
                @if (previewLoading()) {
                  <dbw-state-card
                    appearance="loading"
                    [compact]="true">
                    <p>Analyse des relations de couple en cours.</p>
                  </dbw-state-card>
                }
                @if (previewError()) {
                  <dbw-state-card
                    appearance="error"
                    [compact]="true">
                    <p>{{ previewError() }}</p>
                  </dbw-state-card>
                }
                @if (correctionBlocked()) {
                  <dbw-state-card
                    appearance="error"
                    [compact]="true">
                    <p>La correction rapide est désactivée tant que cette incohérence Atlas n'est pas corrigée.</p>
                  </dbw-state-card>
                }
                @if (canExecuteMetadataCorrectionAction() && !previewLoading() && !preview() && !correctionBlocked()) {
                  <section class="empty-state compact">
                    <p>Choisissez un investisseur à supprimer pour préparer l'exécution.</p>
                  </section>
                }
                @if (preview(); as previewVm) {
                  <div class="preview-summary dbw-card dbw-card--md">
                    <oui-tag
                      type="status"
                      [status]="previewVm.requiresRelationshipReplacement ? 'todo' : 'completed'">
                      {{ previewVm.requiresRelationshipReplacement ? 'Remplacements requis' : 'Exécution directe' }}
                    </oui-tag>
                    <p>{{ previewVm.summaryMessage }}</p>
                  </div>
                  @if (previewVm.requiresRelationshipReplacement) {
                    <div class="replacement-list">
                      @for (option of previewVm.replacementOptions; track option.linkedInvestor.investorId) {
                        <article class="replacement-card dbw-card dbw-card--lg">
                          <dbw-investor-card
                            [id]="option.linkedInvestor.investorId"
                            [code]="option.linkedInvestor.investorCode || 'Code inconnu'"
                            [name]="option.linkedInvestor.displayName || 'Investisseur sans nom'"
                            [statusCode]="option.linkedInvestor.statusCode"></dbw-investor-card>
                          <oui-select
                            label="Investisseur de remplacement"
                            [value]="replacementSelections()[option.linkedInvestor.investorId] ?? null"
                            [options]="replacementOptionChoices()[option.linkedInvestor.investorId] ?? []"
                            (valueChange)="onReplacementSelected(option.linkedInvestor.investorId, $event)">
                          </oui-select>
                        </article>
                      }
                    </div>
                  }
                  @if (canExecuteMetadataCorrectionAction()) {
                    <div class="execution-actions">
                      <oui-button
                        iconSuffix="check_circle"
                        (click)="executeCorrection()"
                        [disabled]="!canExecute()">
                        Exécuter la correction
                      </oui-button>
                    </div>
                  }
                }
              </div>
            } @else {
              <section class="completion-card dbw-card dbw-card--lg">
                <oui-tag
                  type="status"
                  status="completed"
                  >Metadata terminée</oui-tag
                >
                <h5>Aucun doublon restant</h5>
                <p>Le batch peut passer à la metadata suivante.</p>
                <oui-button
                  iconSuffix="arrow_forward"
                  (click)="moveToNext()"
                  [disabled]="!canMoveToNext()">
                  Passer à la suivante
                </oui-button>
              </section>
            }
          }
        </article>
        @if (currentExecution(); as execution) {
          <article class="panel dbw-card dbw-card--xl">
            <div class="panel-heading">
              <div>
                <h4>Dernière exécution</h4>
                <p>Résultat mémorisé pour cette metadata dans la session batch.</p>
              </div>
              <oui-tag
                type="status"
                [status]="execution.success ? 'completed' : 'error'">
                {{ execution.success ? 'Succès' : 'Échec' }}
              </oui-tag>
            </div>
            <div class="result-grid">
              <article class="result-card dbw-card dbw-card--lg">
                <strong>Relations de couple</strong>
                <span>{{ execution.coupleRelationStep.affectedRows }} relation(s)</span>
                <p>{{ execution.coupleRelationStep.message }}</p>
              </article>
              <article class="result-card dbw-card dbw-card--lg">
                <strong>Atlas reset</strong>
                <span>{{ execution.atlasResetStep.affectedRows }} ligne(s)</span>
                <p>{{ execution.atlasResetStep.message }}</p>
              </article>
              <article class="result-card dbw-card dbw-card--lg">
                <strong>Suppression Minotaure</strong>
                <span>{{ execution.minotaureDeletionStep.affectedRows }} ligne(s)</span>
                <p>{{ execution.minotaureDeletionStep.message }}</p>
              </article>
            </div>
          </article>
        }
      </section>
    </section>
  }
</section>
